// src/components/admin/nornsweave/modals/AddOverrideModal.js
import React, { useMemo } from 'react';
import TimePicker from '../../../TimePicker';
import { formatTime12 } from '../../../TimePicker';
import { sendOverrideCancellationEmail } from '../../../../utils/emailService';

function AddOverrideModal({ override, setOverride, onClose, onAdd, showNotification, settingsForm, setSettingsForm, autoSaveSettings, fixedClasses = [], settings }) {
  // Calculate affected fixed classes based on override dates and type
  const affectedClasses = useMemo(() => {
    if (!override.startDate || (override.type !== 'no_classes' && override.type !== 'no_bookings')) {
      return [];
    }

    const startDate = new Date(override.startDate + 'T00:00:00');
    const endDate = override.endDate ? new Date(override.endDate + 'T00:00:00') : startDate;
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Get all days of week in the date range
    const affectedDays = new Set();
    const affectedDatesByDay = {}; // Map of dayOfWeek -> array of dates
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayName = dayNames[currentDate.getDay()];
      affectedDays.add(dayName);
      if (!affectedDatesByDay[dayName]) affectedDatesByDay[dayName] = [];
      affectedDatesByDay[dayName].push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Find fixed classes that occur on affected days
    return fixedClasses
      .filter(c => {
        // Check library match
        const libraryMatch = !override.library || override.library === 'both' ||
          c.library === override.library || c.library === 'both';
        if (!libraryMatch) return false;

        // Check day match - handle both single day and multiple days (A/B days)
        const classDays = Array.isArray(c.dayOfWeek) ? c.dayOfWeek : [c.dayOfWeek];
        return classDays.some(day => affectedDays.has(day));
      })
      .map(c => ({
        ...c,
        affectedDates: Array.isArray(c.dayOfWeek)
          ? c.dayOfWeek.flatMap(day => affectedDatesByDay[day] || [])
          : affectedDatesByDay[c.dayOfWeek] || []
      }));
  }, [override.startDate, override.endDate, override.type, override.library, fixedClasses]);

  const handleAdd = async () => {
    if (!override.startDate || !override.description) {
      showNotification('Please fill in required fields', 'error');
      return;
    }

    // Validate blocked hours if special schedule
    if (override.type === 'special' && override.blockedHours?.length === 0) {
      showNotification('Please add at least one blocked time period', 'error');
      return;
    }

    const newOverride = {
      id: Date.now().toString(),
      ...override,
      endDate: override.endDate || override.startDate
    };
    const newForm = {
      ...settingsForm,
      scheduleOverrides: [...settingsForm.scheduleOverrides, newOverride]
    };
    setSettingsForm(newForm);
    if (autoSaveSettings) autoSaveSettings(newForm);

    // Send cancellation emails to affected teachers if email is enabled
    if (settings?.emailEnabled && affectedClasses.length > 0 &&
        (override.type === 'no_classes' || override.type === 'no_bookings')) {
      const emailPromises = affectedClasses
        .filter(c => c.teacherEmail) // Only send to classes with emails
        .map(c => {
          const classInfo = `${c.grade} Grade - ${Array.isArray(c.dayOfWeek) ? c.dayOfWeek.join('/') : c.dayOfWeek}s ${formatTime12(c.startTime)}`;
          return sendOverrideCancellationEmail({
            teacherEmail: c.teacherEmail,
            teacherName: c.teacherName,
            overrideDescription: override.description,
            startDate: override.startDate,
            endDate: override.endDate || override.startDate,
            affectedDates: c.affectedDates,
            classInfo,
            librarianName: settings?.yourName,
            schoolName: settings?.schoolName,
            librarianEmail: settings?.librarianEmail,
          });
        });

      try {
        await Promise.all(emailPromises);
        showNotification(`Schedule override added. ${affectedClasses.length} teacher(s) notified.`, 'success');
      } catch (err) {
        console.error('Error sending cancellation emails:', err);
        showNotification('Schedule override added, but some emails failed to send.', 'warning');
      }
    } else {
      showNotification('Schedule override added', 'success');
    }

    onClose();
    setOverride({
      startDate: '',
      endDate: '',
      type: 'no_classes',
      description: '',
      useMassDaySchedule: false,
      blockedHours: [],
      library: 'both'
    });
  };

  const addBlockedHour = () => {
    setOverride({
      ...override,
      blockedHours: [...(override.blockedHours || []), { startTime: '', endTime: '', id: Date.now() }]
    });
  };

  const removeBlockedHour = (id) => {
    setOverride({
      ...override,
      blockedHours: (override.blockedHours || []).filter(h => h.id !== id)
    });
  };

  const updateBlockedHour = (id, field, value) => {
    setOverride({
      ...override,
      blockedHours: (override.blockedHours || []).map(h =>
        h.id === id ? { ...h, [field]: value } : h
      )
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Add Schedule Override</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Start Date</label>
              <input
                type="date"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={override.startDate}
                onChange={(e) => setOverride({...override, startDate: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">End Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={override.endDate}
                onChange={(e) => setOverride({...override, endDate: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty for single day</p>
            </div>
          </div>

          <div>
            <label className="block text-slate-700 text-sm font-medium mb-1">Override Type</label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              value={override.type}
              onChange={(e) => setOverride({...override, type: e.target.value, blockedHours: e.target.value === 'special' ? [] : override.blockedHours})}
            >
              <option value="no_classes">No Classes (Admin Bookings OK)</option>
              <option value="no_bookings">No Bookings or Classes</option>
              <option value="mass_day">Mass Day Schedule</option>
              <option value="special">Special Schedule (Block Hours)</option>
            </select>
          </div>

          {/* No Classes Info */}
          {override.type === 'no_classes' && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
              <p className="text-sm text-teal-700">
                Fixed classes will be cancelled and teachers notified. Only admin-type bookings are allowed.
              </p>
              {affectedClasses.length > 0 && (
                <div className="mt-2 pt-2 border-t border-teal-200">
                  <p className="text-xs text-teal-600 font-medium">{affectedClasses.length} class(es) will be cancelled:</p>
                  <ul className="text-xs text-teal-600 mt-1 max-h-20 overflow-y-auto">
                    {affectedClasses.slice(0, 5).map((c, i) => (
                      <li key={i}>• {c.teacherName} ({c.grade})</li>
                    ))}
                    {affectedClasses.length > 5 && <li>...and {affectedClasses.length - 5} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* No Bookings Info */}
          {override.type === 'no_bookings' && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
              <p className="text-sm text-violet-700">
                Fixed classes will be cancelled and teachers notified. No new bookings allowed (except admin).
              </p>
              {affectedClasses.length > 0 && (
                <div className="mt-2 pt-2 border-t border-violet-200">
                  <p className="text-xs text-violet-600 font-medium">{affectedClasses.length} class(es) will be cancelled:</p>
                  <ul className="text-xs text-violet-600 mt-1 max-h-20 overflow-y-auto">
                    {affectedClasses.slice(0, 5).map((c, i) => (
                      <li key={i}>• {c.teacherName} ({c.grade})</li>
                    ))}
                    {affectedClasses.length > 5 && <li>...and {affectedClasses.length - 5} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Mass Day Schedule Toggle */}
          {override.type === 'mass_day' && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
              <p className="text-sm text-violet-700">
                This will apply the Mass Day schedule for the selected date(s).
                Aides with Mass Day schedules will be shown, and regular schedules hidden.
              </p>
            </div>
          )}

          {/* Special Schedule - Block Hours */}
          {override.type === 'special' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <label className="block text-amber-800 text-sm font-medium mb-2">
                Blocked Time Periods
              </label>
              <p className="text-xs text-amber-600 mb-3">
                These hours will be blocked from bookings. Affected classes will need to reschedule.
              </p>

              {(override.blockedHours || []).map((hour) => (
                <div key={hour.id} className="flex items-center gap-2 mb-2">
                  <TimePicker
                    value={hour.startTime}
                    onChange={(val) => updateBlockedHour(hour.id, 'startTime', val)}
                  />
                  <span className="text-slate-500">to</span>
                  <TimePicker
                    value={hour.endTime}
                    onChange={(val) => updateBlockedHour(hour.id, 'endTime', val)}
                  />
                  <button
                    type="button"
                    onClick={() => removeBlockedHour(hour.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addBlockedHour}
                className="w-full py-2 border-2 border-dashed border-amber-300 rounded-lg text-amber-600 hover:bg-amber-100 text-sm"
              >
                + Add Time Block
              </button>
            </div>
          )}

          {/* Library Selection for Special Schedule */}
          {override.type === 'special' && (
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Library Affected</label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={override.library || 'both'}
                onChange={(e) => setOverride({...override, library: e.target.value})}
              >
                <option value="both">Both Libraries</option>
                <option value="elementary">Elementary Only</option>
                <option value="middle">Middle School Only</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-slate-700 text-sm font-medium mb-1">Description</label>
            <input
              type="text"
              required
              placeholder="e.g., State Testing, Book Fair Setup, Assembly"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              value={override.description}
              onChange={(e) => setOverride({...override, description: e.target.value})}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                onClose();
                setOverride({
                  startDate: '',
                  endDate: '',
                  type: 'no_classes',
                  description: '',
                  useMassDaySchedule: false,
                  blockedHours: [],
                  library: 'both'
                });
              }}
              className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              className="flex-1 bg-violet-600 text-white py-2 rounded-lg hover:bg-violet-700"
            >
              Add Override
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddOverrideModal;
